import AddIcon from '@mui/icons-material/Add';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useNavigate } from '@tanstack/react-location';
import { useEffect, useState } from 'react';
import type {
    AirBnBIntegrationConfig,
    PropertyModules,
} from '../../../../../packages/entities/Property';
import ErrorBox from '../../core/components/ErrorBox';
import PageContainer from '../../core/components/PageContainer';
import ResponsiveActionButton from '../../core/components/ResponsiveActionButton';
import { apiRawFetch } from '../../main/client';
import SpinnerIcon from '../../main/component/icon/SpinnerIcon';
import { getEnabledModuleCount } from '../modules/registry';
import PropertyCard from './PropertyCard';
import PropertySettingsDialog from './PropertySettingsDialog';

interface Property {
    id: number;
    name: string;
    minNights: number;
    currency: string;
    country: string;
    weekStartDay: 0 | 1;
    modules: PropertyModules;
    created: string;
    updated: string;
}

const PropertyManagementPage = () => {
    const navigate = useNavigate();
    const isMobile = useMediaQuery('(max-width:768px)');
    const [properties, setProperties] = useState<Property[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [dialogOpen, setDialogOpen] = useState(false);

    const fetchProperties = async () => {
        try {
            setLoading(true);
            const response = await apiRawFetch('api/properties');

            if (!response.ok) {
                throw new Error('Failed to fetch properties');
            }

            const data = await response.json();
            setProperties(data);
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Unknown error');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchProperties();
    }, []);

    const handleCreateProperty = () => {
        setDialogOpen(true);
    };

    const handleEditProperty = (property: Property) => {
        navigate({ to: `/${property.id}/settings/general` });
    };

    const handlePropertySaved = (newPropertyId?: number) => {
        setDialogOpen(false);
        if (newPropertyId) {
            // Navigate to the new property's settings page
            navigate({ to: `/${newPropertyId}/settings/general` });
        } else {
            fetchProperties();
        }
    };

    if (loading) {
        return <SpinnerIcon />;
    }

    if (error) {
        return <ErrorBox error={new Error(error)} />;
    }

    return (
        <PageContainer>
            <Box
                sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    mb: 3,
                }}
            >
                <Typography
                    variant="h2"
                    component="h1"
                    sx={{
                        fontSize: '2rem',
                        textTransform: 'uppercase',
                        letterSpacing: '0.05em',
                    }}
                >
                    Property Management
                </Typography>
                <ResponsiveActionButton
                    label="Add Property"
                    icon={<AddIcon />}
                    onClick={handleCreateProperty}
                />
            </Box>

            {isMobile ? (
                <Stack spacing={2}>
                    {properties.length === 0 ? (
                        <Box
                            sx={{
                                border: '2px solid #374151',
                                backgroundColor: '#1a1a2e',
                                p: 4,
                                textAlign: 'center',
                            }}
                        >
                            <Typography
                                variant="body1"
                                color="text.secondary"
                                sx={{ textTransform: 'uppercase' }}
                            >
                                No properties found
                            </Typography>
                        </Box>
                    ) : (
                        properties.map(property => (
                            <PropertyCard
                                key={property.id}
                                property={property}
                                onClick={() => handleEditProperty(property)}
                            />
                        ))
                    )}
                </Stack>
            ) : (
                <TableContainer
                    component={Paper}
                    elevation={0}
                    sx={{
                        border: '2px solid #374151',
                        borderRadius: 0,
                    }}
                >
                    <Table>
                        <TableHead>
                            <TableRow
                                sx={{
                                    borderBottom: '3px solid #6366f1',
                                    backgroundColor: '#1a1a2e',
                                }}
                            >
                                <TableCell
                                    sx={{
                                        fontWeight: 700,
                                        fontSize: '0.875rem',
                                        textTransform: 'uppercase',
                                        letterSpacing: '0.05em',
                                        py: 2,
                                        px: 2,
                                    }}
                                >
                                    ID
                                </TableCell>
                                <TableCell
                                    sx={{
                                        fontWeight: 700,
                                        fontSize: '0.875rem',
                                        textTransform: 'uppercase',
                                        letterSpacing: '0.05em',
                                        px: 2,
                                    }}
                                >
                                    Name
                                </TableCell>
                                <TableCell
                                    sx={{
                                        fontWeight: 700,
                                        fontSize: '0.875rem',
                                        textTransform: 'uppercase',
                                        letterSpacing: '0.05em',
                                        px: 2,
                                    }}
                                >
                                    Minimum Nights
                                </TableCell>
                                <TableCell
                                    sx={{
                                        fontWeight: 700,
                                        fontSize: '0.875rem',
                                        textTransform: 'uppercase',
                                        letterSpacing: '0.05em',
                                        px: 2,
                                    }}
                                >
                                    Currency
                                </TableCell>
                                <TableCell
                                    sx={{
                                        fontWeight: 700,
                                        fontSize: '0.875rem',
                                        textTransform: 'uppercase',
                                        letterSpacing: '0.05em',
                                        px: 2,
                                    }}
                                >
                                    Modules
                                </TableCell>
                                <TableCell
                                    sx={{
                                        fontWeight: 700,
                                        fontSize: '0.875rem',
                                        textTransform: 'uppercase',
                                        letterSpacing: '0.05em',
                                        px: 2,
                                    }}
                                >
                                    Created
                                </TableCell>
                                <TableCell
                                    sx={{
                                        fontWeight: 700,
                                        fontSize: '0.875rem',
                                        textTransform: 'uppercase',
                                        letterSpacing: '0.05em',
                                        px: 2,
                                    }}
                                >
                                    Updated
                                </TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {properties.length === 0 ? (
                                <TableRow>
                                    <TableCell
                                        colSpan={7}
                                        align="center"
                                        sx={{ py: 6 }}
                                    >
                                        <Typography
                                            variant="body1"
                                            color="text.secondary"
                                            sx={{ textTransform: 'uppercase' }}
                                        >
                                            No properties found
                                        </Typography>
                                    </TableCell>
                                </TableRow>
                            ) : (
                                properties.map(property => (
                                    <TableRow
                                        key={property.id}
                                        onClick={() => handleEditProperty(property)}
                                        sx={{
                                            borderBottom: '1px solid #374151',
                                            transition: 'all 0.2s ease',
                                            cursor: 'pointer',
                                            '&:hover': {
                                                backgroundColor:
                                                    'rgba(99, 102, 241, 0.1)',
                                                transform: 'translateX(2px)',
                                            },
                                        }}
                                    >
                                        <TableCell sx={{ py: 2, px: 2 }}>
                                            <Typography
                                                variant="body2"
                                                fontWeight={600}
                                            >
                                                {property.id}
                                            </Typography>
                                        </TableCell>
                                        <TableCell sx={{ px: 2 }}>
                                            <Typography
                                                variant="body2"
                                                fontWeight={700}
                                            >
                                                {property.name}
                                            </Typography>
                                        </TableCell>
                                        <TableCell sx={{ px: 2 }}>
                                            <Typography variant="body2">
                                                {property.minNights}
                                            </Typography>
                                        </TableCell>
                                        <TableCell sx={{ px: 2 }}>
                                            <Typography variant="body2" fontWeight={600}>
                                                {property.currency}
                                            </Typography>
                                        </TableCell>
                                        <TableCell sx={{ px: 2 }}>
                                            {(() => {
                                                const moduleCount =
                                                    getEnabledModuleCount(
                                                        property.modules
                                                    );
                                                const airbnbModule = property.modules[
                                                    'integration/airbnb'
                                                ] as AirBnBIntegrationConfig | undefined;
                                                const hasAirBnB = airbnbModule?.enabled;

                                                if (moduleCount === 0) {
                                                    return (
                                                        <Typography
                                                            variant="body2"
                                                            color="text.secondary"
                                                        >
                                                            None
                                                        </Typography>
                                                    );
                                                }
                                                return (
                                                    <Stack
                                                        direction="row"
                                                        spacing={1}
                                                        alignItems="center"
                                                    >
                                                        <Box
                                                            sx={{
                                                                display:
                                                                    'inline-block',
                                                                px: 1.5,
                                                                py: 0.5,
                                                                border: '2px solid #6366f1',
                                                                backgroundColor:
                                                                    'rgba(99, 102, 241, 0.1)',
                                                                fontWeight: 700,
                                                                fontSize: '0.75rem',
                                                                textTransform:
                                                                    'uppercase',
                                                                letterSpacing:
                                                                    '0.05em',
                                                                color: '#6366f1',
                                                            }}
                                                        >
                                                            {moduleCount} Active
                                                        </Box>
                                                        {hasAirBnB && (
                                                            <Tooltip
                                                                title={
                                                                    <Box>
                                                                        <Typography
                                                                            sx={{
                                                                                fontWeight: 700,
                                                                                fontSize:
                                                                                    '0.75rem',
                                                                            }}
                                                                        >
                                                                            AirBnB
                                                                            Listing:{' '}
                                                                            {
                                                                                airbnbModule.listingId
                                                                            }
                                                                        </Typography>
                                                                        {airbnbModule.lastSyncAt && (
                                                                            <Typography
                                                                                sx={{
                                                                                    fontSize:
                                                                                        '0.7rem',
                                                                                    color: '#9ca3af',
                                                                                }}
                                                                            >
                                                                                Last
                                                                                sync:{' '}
                                                                                {new Date(
                                                                                    airbnbModule.lastSyncAt
                                                                                ).toLocaleString()}
                                                                            </Typography>
                                                                        )}
                                                                    </Box>
                                                                }
                                                                arrow
                                                            >
                                                                <Box
                                                                    sx={{
                                                                        display:
                                                                            'inline-block',
                                                                        px: 1,
                                                                        py: 0.25,
                                                                        border: '2px solid',
                                                                        borderColor:
                                                                            airbnbModule.syncStatus ===
                                                                            'success'
                                                                                ? '#22c55e'
                                                                                : airbnbModule.syncStatus ===
                                                                                    'error'
                                                                                  ? '#ef4444'
                                                                                  : '#f59e0b',
                                                                        backgroundColor:
                                                                            airbnbModule.syncStatus ===
                                                                            'success'
                                                                                ? 'rgba(34, 197, 94, 0.1)'
                                                                                : airbnbModule.syncStatus ===
                                                                                    'error'
                                                                                  ? 'rgba(239, 68, 68, 0.1)'
                                                                                  : 'rgba(245, 158, 11, 0.1)',
                                                                        fontWeight: 700,
                                                                        fontSize:
                                                                            '0.65rem',
                                                                        textTransform:
                                                                            'uppercase',
                                                                        letterSpacing:
                                                                            '0.05em',
                                                                        color:
                                                                            airbnbModule.syncStatus ===
                                                                            'success'
                                                                                ? '#22c55e'
                                                                                : airbnbModule.syncStatus ===
                                                                                    'error'
                                                                                  ? '#ef4444'
                                                                                  : '#f59e0b',
                                                                        cursor: 'help',
                                                                    }}
                                                                >
                                                                    AirBnB
                                                                </Box>
                                                            </Tooltip>
                                                        )}
                                                    </Stack>
                                                );
                                            })()}
                                        </TableCell>
                                        <TableCell sx={{ px: 2 }}>
                                            <Typography variant="body2">
                                                {new Date(
                                                    property.created
                                                ).toLocaleDateString()}
                                            </Typography>
                                        </TableCell>
                                        <TableCell sx={{ px: 2 }}>
                                            <Typography variant="body2">
                                                {new Date(
                                                    property.updated
                                                ).toLocaleDateString()}
                                            </Typography>
                                        </TableCell>
                                    </TableRow>
                                ))
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
            )}

            <PropertySettingsDialog
                open={dialogOpen}
                property={null}
                onClose={() => setDialogOpen(false)}
                onSaved={handlePropertySaved}
            />
        </PageContainer>
    );
};

export default PropertyManagementPage;
